import type { InputHTMLAttributes } from 'react'
import { forwardRef, useId } from 'react'

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /**
   * The login design shows placeholders only, with no visible labels. A real
   * label is still rendered and visually hidden — a placeholder is not an
   * accessible name, and it disappears the moment someone starts typing.
   */
  label: string
  /**
   * 'hidden' (default) is the login style: filled field, sr-only label,
   * placeholder as the visual cue. 'floating' is the checkout style: outlined
   * field with the label always visible, small, inside the top of the box —
   * so no placeholder is needed.
   */
  labelStyle?: 'hidden' | 'floating'
  invalid?: boolean
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, labelStyle = 'hidden', invalid = false, id, ...props },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId

  if (labelStyle === 'floating') {
    return (
      <div className="relative">
        <label
          htmlFor={inputId}
          className="pointer-events-none absolute top-2.5 left-4 text-xs text-field-ink"
        >
          {label}
        </label>
        <input
          {...props}
          ref={ref}
          id={inputId}
          aria-invalid={invalid || undefined}
          className="h-15 w-full rounded-field border border-black/10 bg-white px-4 pt-6 pb-1.5 text-base text-ink outline-none transition focus-visible:ring-2 focus-visible:ring-brand-ring disabled:opacity-60"
        />
      </div>
    )
  }

  return (
    <div>
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <input
        {...props}
        ref={ref}
        id={inputId}
        aria-invalid={invalid || undefined}
        className="h-12 w-full rounded-field bg-field px-4 text-base text-ink outline-none transition placeholder:text-field-ink focus-visible:ring-2 focus-visible:ring-brand-ring disabled:opacity-60"
      />
    </div>
  )
})
